/* eslint-disable react/jsx-wrap-multilines */
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormControlLabel, Grid, Paper, Radio, RadioGroup } from '@material-ui/core';
import { InfoFormControl } from 'components/atoms';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import AddressService from 'services/AddressService';
import { useAuth } from 'context';
import GroupAddressSelect from 'components/mocules/GroupAddressSelect';
import { ADDRESS_CHANGE_TYPE } from 'constants/Enums';
import clsx from 'clsx';
import InfoInput from '../InfoInput';
import styles from './styles.module.css';

const DEFAULT_INVOICE_INFO = {
  code: '',
  companyName: '',
  companyAddress: '',
  taxCode: '',
  postCode: '',
  province: '',
  district: '',
  ward: '',
  streetAddress: '',
};

const invoiceInfoOtherID = uuidv4();
const InvoiceCheckout = ({
  invoiceInfoList = [],
  handleSetAllInvoice,
  invoice = {},
  translateCheckout,
  currentAddress,
  cart,
  customerShippingAddress,
  error,
  setError,
}) => {
  const {
    companyAddress = '',
    companyName = '',
    mst = '',
    taxCode = '',
  } = invoice || {}; // invoice from cart info

  const invoiceInfoDefault =
    (invoiceInfoList && invoiceInfoList[0] && invoiceInfoList[0].code) || invoiceInfoOtherID;
  const [invoiceAddress, setInvoiceAddress] = useState({
    province: '',
    district: '',
    ward: '',
    changeType: ADDRESS_CHANGE_TYPE.ASSIGNED,
  });

  const [invoiceInfoSelected, setInvoiceInfoSelected] = useState(invoiceInfoDefault);
  const [isCheckedRequestInvoice, setIsCheckedRequestInvoice] = useState(false);
  const [showInvoiceInfo, setShowInvoiceInfo] = useState(false);
  const isInvoiceInfoOther = invoiceInfoSelected === invoiceInfoOtherID;
  const { customerInfo } = useAuth();
  const { email } = customerInfo || {};

  useEffect(() => {
    // Find invoice base on invoice cart info
    const invoiceInfo = invoiceInfoList.find(
      (item) =>
        item.companyAddress === companyAddress &&
        item.companyName === companyName &&
        (mst || taxCode) === item.taxCode,
    );
    if (invoiceInfo && invoiceInfo.code) {
      setInvoiceInfoSelected(invoiceInfo.code);
    } else {
      setInvoiceInfoSelected(invoiceInfoOtherID);
    }
  }, []);

  // The saved invoice info owns its post code. The cart's post code only fills in when
  // that invoice info has none and it is the one the cart is using.
  const getInvoicePostCode = (info) => {
    if (info?.postCode) return info.postCode;
    const cartInvoice = cart?.invoice || {};
    const isCartInvoice =
      info?.companyAddress === cartInvoice.companyAddress &&
      info?.companyName === cartInvoice.companyName &&
      (cartInvoice.mst || cartInvoice.taxCode) === info?.taxCode;
    return (isCartInvoice && cartInvoice.postCode) || '';
  };

  const handleChangeInvoiceInfoSelected = (e) => {
    const { value = invoiceInfoOtherID } = e.target;
    setInvoiceInfoSelected(value);
    if (value !== invoiceInfoOtherID) {
      const info = invoiceInfoList.find((item) => item.code === value);
      handleSetAllInvoice({
        invoiceRequest: true,
        isUseCustom: false,
        ...info,
        postCode: getInvoicePostCode(info),
      });
    } else {
      handleSetAllInvoice({
        invoiceRequest: true,
        isUseCustom: true,
        ...DEFAULT_INVOICE_INFO,
      });
    }
  };

  // {} === {} -> false || new Set array like obj with JSON.
  const uniqueInvoiceList = [...new Set(invoiceInfoList?.map(JSON.stringify))].map(JSON.parse);

  const handleChangeCheckboxRequestInvoice = async (event) => {
    const isRequestInvoice = event.target.value === 'true';
    setIsCheckedRequestInvoice(event.target.value);
    setShowInvoiceInfo(isRequestInvoice);
    setInvoiceInfoSelected(null);
    setError('invoiceRequest', false);

    // Lấy địa chỉ mới trước khi sử dụng
    const resFullAddress = await AddressService.getMasterAddressObject({
      ctx: null,
      provinceCode: currentAddress?.province,
      districtCode: currentAddress?.district,
      wardCode: currentAddress?.ward,
    });

    const newFullAddress = `${customerShippingAddress}, ${resFullAddress.ward}, ${resFullAddress.district}, ${resFullAddress.province}`;

    if (!isRequestInvoice) {
      handleSetAllInvoice({
        ...DEFAULT_INVOICE_INFO,
        invoiceRequest: false,
        companyName: cart?.customerName,
        companyAddress: newFullAddress,
      });
    } else {
      handleChangeInvoiceInfoSelected({ target: { value: invoiceInfoDefault } });
    }
  };

  const handleSetCurrentAddress = async (address) => {
    setInvoiceAddress(address);

    if (address.changeType === ADDRESS_CHANGE_TYPE.PROVINCE_CHANGE) {
      if (address.province === '0') {
        handleSetAllInvoice({ ...invoice, province: '', district: '', ward: '' });
      } else {
        handleSetAllInvoice({ ...invoice, province: address.province });
      }
      setError('province', false);
    }

    if (address.changeType === ADDRESS_CHANGE_TYPE.DISTRICT_CHANGE) {
      if (address.district === '0') {
        handleSetAllInvoice({ ...invoice, district: '', ward: '' });
      } else {
        handleSetAllInvoice({ ...invoice, district: address.district });
      }
      setError('district', false);
    }

    if (address.changeType === ADDRESS_CHANGE_TYPE.WARD_CHANGE) {
      if (address.ward === '0') {
        handleSetAllInvoice({ ...invoice, ward: '' });
      } else {
        // Lấy địa chỉ mới trước khi sử dụng
        const res = await AddressService.getMasterAddressObject({
          ctx: null,
          provinceCode: address?.province,
          districtCode: address?.district,
          wardCode: address?.ward,
        });

        const newFullAddress = `${invoice.streetAddress}, ${res.ward}, ${res.district}, ${res.province}`;
        handleSetAllInvoice({
          ...invoice,
          companyAddress: newFullAddress,
          ward: address.ward,
        });
      }
      setError('ward', false);
    }
  };

  const handleChangeInvoice = (key, newValue) => {
    const newdataInvoice = { ...invoice, [key]: newValue };
    handleSetAllInvoice(newdataInvoice);
    setError(key, false);
  };

  return (
    <Paper
      elevation={4}
      className={clsx(styles.paperroot, { [styles.error]: !!error.invoiceRequest })}
    >
      {/* 29Dec2023 - tax info is required https://buymed.atlassian.net/browse/B2B-1055 */}
      {/* <Accordion expanded={expanded} className={styles.root} elevation={4}>
        <AccordionSummary aria-controls="panel1a-content" id="panel1a-header">
          <Typography>
            <FormControlLabel
              control={
                <Checkbox classes={{ root: styles.checkbox }} checked={expanded} onChange={handleExpand} data-test="checkbox-checkout-invoice" />
              }
              label={translateCheckout('export_invoice_info')}
            />
          </Typography>
        </AccordionSummary> */}
      {/* <AccordionDetails> */}
      <Grid container data-test="list-invoice-container" className={styles.root}>
        <h1 className={styles.title}>
          {translateCheckout('export_invoice_info')}
          <i style={{ color: '#FF0000' }}>*</i>
        </h1>

        <div>
          <RadioGroup
            aria-label="isCheckedRequestInvoice"
            name="isCheckedRequestInvoice"
            value={isCheckedRequestInvoice}
            onChange={handleChangeCheckboxRequestInvoice}
          >
            <FormControlLabel
              value="false"
              control={<Radio />}
              label={translateCheckout('do_not_request_tax_invoice')}
            />
            <FormControlLabel
              value="true"
              control={<Radio />}
              label={translateCheckout('request_tax_invoice_via_email')}
            />
          </RadioGroup>
          {showInvoiceInfo && (
            <InfoFormControl xs={12} isRequired label={translateCheckout('email')} htmlFor="email">
              <InfoInput
                id="email"
                // placeholder={translateCheckout('at_least_2_character')}
                onBlur={(e) => handleChangeInvoice('email', e.target.value)}
                defaultValue={email || invoice.email}
                required
                name="email"
                className={styles.inputInvoice}
              />
            </InfoFormControl>
          )}
        </div>

        <Grid item xs={12} className={styles.invoiceList}>
          <RadioGroup
            aria-label="gender"
            name="gender1"
            value={invoiceInfoSelected}
            onChange={handleChangeInvoiceInfoSelected}
          >
            {showInvoiceInfo &&
              uniqueInvoiceList.map((item) => (
                <>
                  <FormControlLabel
                    key={item.code}
                    control={<Radio name="checkedC" value={item.code} />}
                    label={
                      <div>
                        <i> {item.companyName}</i> -<b> {item.taxCode} </b> ({item.companyAddress})
                      </div>
                    }
                  />
                  {item.code === invoiceInfoSelected && (
                    <InfoFormControl
                      xs={12}
                      isRequired
                      label={translateCheckout('post_code')}
                      htmlFor="postCode"
                    >
                      <InfoInput
                        id="postCode"
                        placeholder={translateCheckout('post_code')}
                        onBlur={(e) => handleChangeInvoice('postCode', e.target.value)}
                        defaultValue={getInvoicePostCode(item)}
                        required
                        name="postCode"
                        className={styles.inputInvoice}
                      />
                    </InfoFormControl>
                  )}
                </>
              ))}

            {showInvoiceInfo && (!invoiceInfoList || invoiceInfoList.length < 10) && (
              <FormControlLabel
                control={<Radio name="checkedC" value={invoiceInfoOtherID} />}
                label={
                  <i>
                    <b>{translateCheckout('other')}</b>
                  </i>
                }
              />
            )}
          </RadioGroup>
          {isInvoiceInfoOther && (
            <>
              <InfoFormControl
                xs={12}
                isRequired
                label={translateCheckout('company_name')}
                htmlFor="companyName"
              >
                <InfoInput
                  id="companyName"
                  placeholder={translateCheckout('at_least_2_character')}
                  onBlur={(e) => handleChangeInvoice('companyName', e.target.value)}
                  // defaultValue={companyName}
                  required
                  name="companyName"
                  error={!!error.companyName}
                />
              </InfoFormControl>
              <InfoFormControl
                xs={12}
                isRequired
                label={translateCheckout('tax_code')}
                htmlFor="taxCode"
              >
                <InfoInput
                  id="taxCode"
                  placeholder={translateCheckout('tax_code')}
                  onBlur={(e) => handleChangeInvoice('taxCode', e.target.value)}
                  required
                  name="taxCode"
                  error={!!error.taxCode}
                />
              </InfoFormControl>
              <InfoFormControl
                xs={12}
                isRequired
                label={translateCheckout('enter_company_address_label')}
                htmlFor="address"
              >
                <InfoInput
                  id="streetAddress"
                  type="text"
                  name="streetAddress"
                  variant="outlined"
                  aria-label="streetAddress"
                  placeholder={translateCheckout('enter_company_address')}
                  onBlur={(e) => handleChangeInvoice('streetAddress', e.target.value)}
                  required
                  error={!!error.streetAddress}
                />
              </InfoFormControl>
              <GroupAddressSelect
                id={uuidv4()}
                currentAddress={invoiceAddress}
                handleSetCurrentAddress={handleSetCurrentAddress}
                style={{ margonTop: 0 }}
                error={{ province: error?.province, district: error?.district, ward: error?.ward }}
              />
              <InfoFormControl
                xs={12}
                isRequired
                label={translateCheckout('post_code')}
                htmlFor="postCode"
              >
                <InfoInput
                  id="postCode"
                  type="number"
                  placeholder={translateCheckout('post_code')}
                  onBlur={(e) => handleChangeInvoice('postCode', e.target.value)}
                  required
                  name="postCode"
                  error={!!error.postCode}
                />
              </InfoFormControl>
              <div className={styles.helper_text}>
                <small className={styles.text_muted}>
                  <FontAwesomeIcon icon={faInfoCircle} className={styles.icon} />
                  <span className={styles.fw500}>{translateCheckout('notice')}: </span>
                  <ul className={styles.notifi_list}>
                    <li>{translateCheckout('fill_correct_invoice_info_1')}</li>
                    <li>{translateCheckout('fill_correct_invoice_info_2')}</li>
                    {/* <li>{translateCheckout('export_invoice_text').replace('{BRAND_NAME}', BRAND_NAME)}</li> */}
                  </ul>
                </small>
              </div>
            </>
          )}
        </Grid>
        {/* {!isInvoiceInfoOther && (
              <div className={styles.helper_text} style={{ marginTop: 10 }}>
                <small className={styles.text_muted}>
                  <FontAwesomeIcon icon={faInfoCircle} className={styles.icon} />
                  <span className={styles.fw500}>
                    {translateCheckout('fill_correct_invoice_info')}: {translateCheckout('export_invoice_text').replace('{BRAND_NAME}', BRAND_NAME)}
                  </span>
                </small>
              </div>
            )} */}
      </Grid>
      {/* </AccordionDetails> */}
      {/* </Accordion> */}
    </Paper>
  );
};

export default InvoiceCheckout;
